import React, { FC, useEffect, useMemo, useState } from "react";
import { getIsTourShownFirstTime } from "./helpers/getIsTourWasShown";
import ProductTour from "./ProductTour";
import TakeTourFloatingButton from "./components/TakeTourFloatingButton";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { useLocation } from "react-router-dom";
import { RoutesE } from "../../routes/routesData";
import getUserType from "./helpers/getUserType";
import { setTourGateStatus } from "../../store/slices/productTourSlice";
import { TourGateStatusE } from "../../models/enums";

/**
 * Find a name and ID for the tour
 * Check is the tour already shown
 * Add "Take a tour" floating button to the page
 * Report to the tour gate whether the welcome message / tour is holding the screen
 * */

type ProductTourWrapperPropTypes = {
  isPageReady?: boolean;
  tourName?: string;
  withSplashScreen?: boolean;
  initialStep?: number;
}

const ProductTourWrapper: FC<ProductTourWrapperPropTypes> = (props) => {
  const { isPageReady = true, tourName = 'campaigns', withSplashScreen, initialStep = 0 } = props;
  const user: any = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const tourNameWithUser = useMemo(() => user ? tourName + ' ' + getUserType(user?.clientType) : tourName, [user?.clientType, tourName]);
  // Make a tour name based on current page name
  // const tourName = window.location.pathname;
  // Make a tour ID which consist of tour name and current user ID
  const tourID = useMemo(() => `tour-${tourNameWithUser}-${user?.userId}`, [tourNameWithUser, user?.userId]);

  // Check if current tourID already exist within local storage
  const [isShownFirstTime, setIsShownFirstTime] = useState(getIsTourShownFirstTime(tourID));

useEffect(() => {
  setIsShownFirstTime(getIsTourShownFirstTime(tourID));
}, [user?.clientType, tourID, tourNameWithUser]);

  // Whether <ProductTour/> below will actually put something on screen — the welcome message first,
  // then the tour steps. `isShownFirstTime` covers the whole run: ProductTour only flips it to false
  // once the user leaves the tour ("Not now", the tooltip's X / Esc, or finishing the last step).
  // `user?.userId` rather than `user`: ProductTour renders nothing without it, so treating such a
  // session as "tour on screen" would block the gate on an overlay that never appears.
  const isTourOnScreen = !!user?.userId && isPageReady && isShownFirstTime && !isMobileOrTablet();

  // The tour paints above every popup in the app (intro.js sits at z-index 100,000,000), so a window
  // opened while it runs is buried underneath it — the case that made the welcome message and the
  // global notification popup pile up on first login. Publish the state instead of letting each
  // overlay guess: useGlobalNotification holds its popup back until this reads Cleared.
  useEffect(() => {
    dispatch(
      setTourGateStatus(
        isTourOnScreen ? TourGateStatusE.Blocking : TourGateStatusE.Cleared
      )
    );
  }, [isTourOnScreen, dispatch]);

  // Whatever unmounts the wrapper (logout, a translations reload) also takes the tour off screen, so
  // release the gate on the way out — a notification held back must never be stranded by a teardown.
  useEffect(
    () => () => {
      dispatch(setTourGateStatus(TourGateStatusE.Cleared));
    },
    [dispatch]
  );

  // Show tour only for the desktop
  if (isMobileOrTablet()) return null;

  //TODO: implement tours for mobile and tablets

  return user ? (
    isPageReady && (
      <>
        {location.pathname.includes(RoutesE.CAMPAIGNS) && (
          <TakeTourFloatingButton
            setIsShownFirstTime={setIsShownFirstTime}
            tourName={tourNameWithUser}
          />
        )}
        {isShownFirstTime && (
          <ProductTour
            tourName={tourNameWithUser}
            tourID={tourID}
            setIsShownFirstTime={setIsShownFirstTime}
            withSplashScreen={withSplashScreen}
            initialStep={initialStep}
          />
        )}
      </>
    )
  ) : <></>;
};

export default ProductTourWrapper;
